/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Visibility,
  Favorite,
  Movie,
  AttachMoney,
  People,
  Timeline,
  Refresh,
  Info,
  Whatshot,
  FilterList,
  Star,
  CalendarToday,
  Warning,
  Handshake,
  Speed
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { brandColors } from '../../../theme/theme';
import { getFilmDetails } from '../services/filmTrackingService';
import { transformSurveyWaves, getLatestWave, getTrendData, getFilterOptions } from '../../../lib/utils/filmMetricsUtils';
import { formatDateForDisplay } from '../../../lib/utils/dateUtils';
import apiService from '../../../lib/services/apiService';
import { ReportDownloadButton } from '../../../components/ReportGeneration';
import FilmCompsContainer from '../../../components/FilmComps/FilmCompsContainer';
import ComparisonContainer from '../../../components/Comparison/ComparisonContainer';
import BoxOfficeProjectionsContainer from '../../../components/BoxOfficeProjections/BoxOfficeProjectionsContainer';
import { useFilmSubscription } from '../../../hooks/useFilmSubscription';
import BlurredContent from '../../../components/Subscription/BlurredContent';
import { FILM_TIERS } from '../../../lib/filmSubscriptionTiers';

// Deep Dive Components - Removed (moved to standalone page)
// Deep Dive Utilities - Removed (moved to standalone page)

const FilmDetail = () => {
  const { ttId } = useParams();
  const navigate = useNavigate();
  const { canAccessFilmDetail } = useFilmSubscription();
  
  // Core data state
  const [surveyWaves, setSurveyWaves] = useState([]);
  const [imdbData, setImdbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    movieFrequency: '',
    ageGroup: '',
    gender: '',
    ethnicity: '',
    surveyDateFrom: '',
    surveyDateTo: ''
  });
  
  // Computed data
  const transformedWaves = transformSurveyWaves(surveyWaves);
  const latestWave = getLatestWave(transformedWaves);
  const filterOptions = getFilterOptions();

  // Fetch film data
  const fetchData = async (customFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const activeFilters = customFilters || filters;
      
      // Build API filters
      const apiFilters = {};
      if (activeFilters.movieFrequency) apiFilters.movie_frequency_category = activeFilters.movieFrequency;
      if (activeFilters.ageGroup) apiFilters.agegroup = activeFilters.ageGroup;
      if (activeFilters.gender) apiFilters.gender = activeFilters.gender;
      if (activeFilters.ethnicity) apiFilters.ethnicity = activeFilters.ethnicity;
      if (activeFilters.surveyDateFrom) apiFilters.survey_date_from = activeFilters.surveyDateFrom;
      if (activeFilters.surveyDateTo) apiFilters.survey_date_to = activeFilters.surveyDateTo;
      
      const response = await getFilmDetails(ttId, apiFilters);
      
      console.log('🎬 Film Detail Response:', {
        surveyWavesCount: response.survey?.data?.length || 0,
        firstWave: response.survey?.data?.[0],
        filters: apiFilters
      });
      
      setSurveyWaves(response.survey.data || []);
      setImdbData(response.imdb?.data || null);
      
    } catch (err) {
      console.error('Error fetching film details:', err);
      setError('Failed to load film details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ttId) {
      fetchData();
    }
  }, [ttId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter handlers
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      movieFrequency: '',
      ageGroup: '',
      gender: '',
      ethnicity: '',
      surveyDateFrom: '',
      surveyDateTo: ''
    };
    setFilters(emptyFilters);
    fetchData(emptyFilters);
  };

  // Deep Dive functions removed - feature moved to standalone page at /film-tracking/deep-dive

  // Segment Metric Card Component
  const SegmentMetricCard = ({ data, title }) => {
    if (!data) {
      return (
        <Card sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Card>
      );
    }

    return (
      <Card sx={{ height: 200, border: `2px solid ${brandColors.primary}20` }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
            {title}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('awareness'), fontSize: '1rem' }}>
                  {data.pctAware}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Aware
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('interest'), fontSize: '1rem' }}>
                  {data.pctInterest}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Interest
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('theater'), fontSize: '1rem' }}>
                  {data.pctTheater}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Theater
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('pay'), fontSize: '1rem' }}>
                  {data.pctWillingToPay}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Pay
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              n={data.totalResponses?.toLocaleString() || 0}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Combined Metric Card (for totals)
  const CombinedMetricCard = ({ maleData, femaleData, title }) => {
    if (!maleData && !femaleData) {
      return (
        <Card sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Card>
      );
    }

    // Average the metrics (simple approach)
    const avgAware = Math.round(((maleData?.pctAware || 0) + (femaleData?.pctAware || 0)) / 2);
    const avgInterest = Math.round(((maleData?.pctInterest || 0) + (femaleData?.pctInterest || 0)) / 2);
    const avgTheater = Math.round(((maleData?.pctTheater || 0) + (femaleData?.pctTheater || 0)) / 2);
    const avgPay = Math.round(((maleData?.pctWillingToPay || 0) + (femaleData?.pctWillingToPay || 0)) / 2);
    const totalResponses = (maleData?.totalResponses || 0) + (femaleData?.totalResponses || 0);

    return (
      <Card sx={{ height: 200, border: `2px solid ${brandColors.secondary}20` }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
            {title}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('awareness'), fontSize: '1rem' }}>
                  {avgAware}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Aware
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('interest'), fontSize: '1rem' }}>
                  {avgInterest}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Interest
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('theater'), fontSize: '1rem' }}>
                  {avgTheater}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Theater
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('pay'), fontSize: '1rem' }}>
                  {avgPay}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Pay
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              n={totalResponses.toLocaleString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Grand Total Card
  const GrandTotalCard = ({ allData, title }) => {
    if (!allData || allData.length === 0) {
      return (
        <Card sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Card>
      );
    }

    // Calculate grand totals
    const totalAware = allData.reduce((sum, data) => sum + (data?.pctAware || 0), 0);
    const totalInterest = allData.reduce((sum, data) => sum + (data?.pctInterest || 0), 0);
    const totalTheater = allData.reduce((sum, data) => sum + (data?.pctTheater || 0), 0);
    const totalPay = allData.reduce((sum, data) => sum + (data?.pctWillingToPay || 0), 0);
    const totalResponses = allData.reduce((sum, data) => sum + (data?.totalResponses || 0), 0);
    const validDataCount = allData.filter(data => data && data.totalResponses > 0).length;

    const avgAware = validDataCount > 0 ? Math.round(totalAware / validDataCount) : 0;
    const avgInterest = validDataCount > 0 ? Math.round(totalInterest / validDataCount) : 0;
    const avgTheater = validDataCount > 0 ? Math.round(totalTheater / validDataCount) : 0;
    const avgPay = validDataCount > 0 ? Math.round(totalPay / validDataCount) : 0;

    return (
      <Card sx={{ height: 200, border: `3px solid ${brandColors.gold}`, backgroundColor: brandColors.gold + '05' }}>
        <CardContent sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', textAlign: 'center', color: brandColors.gold }}>
            {title}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('awareness'), fontSize: '1rem' }}>
                  {avgAware}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Aware
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('interest'), fontSize: '1rem' }}>
                  {avgInterest}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Interest
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('theater'), fontSize: '1rem' }}>
                  {avgTheater}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Theater
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: getMetricColor('pay'), fontSize: '1rem' }}>
                  {avgPay}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Pay
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              n={totalResponses.toLocaleString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };


  // Radar Comparison Component
  const RadarComparison = ({ gridData }) => {
    const segments = [
      { key: 'maleUnder35', label: 'Male Under 35', color: '#1976d2' },
      { key: 'femaleUnder35', label: 'Female Under 35', color: '#d32f2f' },
      { key: 'maleOver35', label: 'Male Over 35', color: '#388e3c' },
      { key: 'femaleOver35', label: 'Female Over 35', color: '#f57c00' }
    ];

    // Prepare radar chart data
    const radarData = [
      { metric: 'Awareness', fullMark: 100 },
      { metric: 'Interest', fullMark: 100 },
      { metric: 'Theater', fullMark: 100 },
      { metric: 'Pay', fullMark: 100 },
      { metric: 'Heat', fullMark: 100 },
      { metric: 'PLF', fullMark: 100 }
    ];

    // Add segment data to radar chart
    segments.forEach(segment => {
      const data = getLatestWave(gridData[segment.key]);
      if (data) {
        radarData[0][segment.key] = data.pctAware || 0;
        radarData[1][segment.key] = data.pctInterest || 0;
        radarData[2][segment.key] = data.pctTheater || 0;
        radarData[3][segment.key] = data.pctWillingToPay || 0;
        radarData[4][segment.key] = data.pctHeat || 0;
        radarData[5][segment.key] = data.pctPLF || 0;
      }
    });

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Radar Chart */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                  Multi-Segment Performance Radar
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]} 
                      tickFormatter={(value) => `${value}%`}
                    />
                    {segments.map(segment => {
                      const data = getLatestWave(gridData[segment.key]);
                      return data ? (
                        <Radar
                          key={segment.key}
                          name={segment.label}
                          dataKey={segment.key}
                          stroke={segment.color}
                          fill={segment.color}
                          fillOpacity={0.1}
                          strokeWidth={3}
                        />
                      ) : null;
                    })}
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Insights Panel */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  🤖 Smart Insights
                </Typography>
                <AutomatedInsights gridData={gridData} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Automated Insights Component
  const AutomatedInsights = ({ gridData }) => {
    const segments = [
      { key: 'maleUnder35', label: 'Male Under 35' },
      { key: 'femaleUnder35', label: 'Female Under 35' },
      { key: 'maleOver35', label: 'Male Over 35' },
      { key: 'femaleOver35', label: 'Female Over 35' }
    ];

    const insights = [];
    
    // Find highest and lowest performing segments
    const segmentData = segments.map(segment => ({
      ...segment,
      data: getLatestWave(gridData[segment.key])
    })).filter(segment => segment.data);

    if (segmentData.length > 0) {
      // Awareness insights
      const awarenessSorted = [...segmentData].sort((a, b) => (b.data.pctAware || 0) - (a.data.pctAware || 0));
      if (awarenessSorted.length >= 2) {
        const highest = awarenessSorted[0];
        const lowest = awarenessSorted[awarenessSorted.length - 1];
        const diff = (highest.data.pctAware || 0) - (lowest.data.pctAware || 0);
        
        if (diff > 10) {
          insights.push({
            type: 'awareness',
            icon: <Visibility />,
            color: getMetricColor('awareness'),
            text: `${highest.label} shows ${diff}pp higher awareness than ${lowest.label} (${highest.data.pctAware}% vs ${lowest.data.pctAware}%)`
          });
        }
      }

      // Interest insights
      const interestSorted = [...segmentData].sort((a, b) => (b.data.pctInterest || 0) - (a.data.pctInterest || 0));
      if (interestSorted.length >= 2) {
        const highest = interestSorted[0];
        const lowest = interestSorted[interestSorted.length - 1];
        const diff = (highest.data.pctInterest || 0) - (lowest.data.pctInterest || 0);
        
        if (diff > 8) {
          insights.push({
            type: 'interest',
            icon: <Favorite />,
            color: getMetricColor('interest'),
            text: `${highest.label} shows ${diff}pp higher interest than ${lowest.label} (${highest.data.pctInterest}% vs ${lowest.data.pctInterest}%)`
          });
        }
      }

      // Theater intent insights
      const theaterSorted = [...segmentData].sort((a, b) => (b.data.pctTheater || 0) - (a.data.pctTheater || 0));
      if (theaterSorted.length >= 2) {
        const highest = theaterSorted[0];
        const lowest = theaterSorted[theaterSorted.length - 1];
        const diff = (highest.data.pctTheater || 0) - (lowest.data.pctTheater || 0);
        
        if (diff > 8) {
          insights.push({
            type: 'theater',
            icon: <Movie />,
            color: getMetricColor('theater'),
            text: `${highest.label} shows ${diff}pp higher theater intent than ${lowest.label} (${highest.data.pctTheater}% vs ${lowest.data.pctTheater}%)`
          });
        }
      }

      // Sample size warnings
      const smallSampleSegments = segmentData.filter(segment => (segment.data.totalResponses || 0) < 100);
      if (smallSampleSegments.length > 0) {
        insights.push({
          type: 'warning',
          icon: <Info />,
          color: '#ff9800',
          text: `Small sample warning: ${smallSampleSegments.map(s => s.label).join(', ')} have <100 responses. Results may be less reliable.`
        });
      }
    }

    return (
      <Box>
        {insights.length > 0 ? (
          insights.map((insight, index) => (
            <Alert 
              key={index}
              severity={insight.type === 'warning' ? 'warning' : 'info'}
              icon={insight.icon}
              sx={{ 
                mb: 2,
                '& .MuiAlert-icon': { color: insight.color }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {insight.text}
              </Typography>
            </Alert>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            Analyzing demographic patterns...
          </Typography>
        )}
        
        {/* Performance Summary */}
        {segmentData.length > 0 && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: brandColors.primary + '05', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              📊 Performance Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {segmentData.length} demographic segments analyzed • 
              Avg awareness: {Math.round(segmentData.reduce((sum, s) => sum + (s.data.pctAware || 0), 0) / segmentData.length)}% • 
              Avg interest: {Math.round(segmentData.reduce((sum, s) => sum + (s.data.pctInterest || 0), 0) / segmentData.length)}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Demographic Trend Analysis Component
  const DemographicTrendAnalysis = ({ gridData }) => {
    const segments = [
      { key: 'maleUnder35', label: 'Male Under 35', color: '#1976d2' },
      { key: 'femaleUnder35', label: 'Female Under 35', color: '#d32f2f' },
      { key: 'maleOver35', label: 'Male Over 35', color: '#388e3c' },
      { key: 'femaleOver35', label: 'Female Over 35', color: '#f57c00' }
    ];

    const [selectedMetric, setSelectedMetric] = useState('pctAware');
    const [selectedSegments, setSelectedSegments] = useState(['maleUnder35', 'femaleUnder35']);

    const metricOptions = [
      { value: 'pctAware', label: 'Awareness' },
      { value: 'pctInterest', label: 'Interest' },
      { value: 'pctTheater', label: 'Theater Intent' },
      { value: 'pctWillingToPay', label: 'Willing to Pay' },
      { value: 'pctHeat', label: 'Heat Score' },
      { value: 'pctPLF', label: 'Premium Format' }
    ];

    // Prepare trend data for selected segments
    const trendData = [];
    const maxWaves = Math.max(...Object.values(gridData).map(waves => waves?.length || 0));
    
    for (let waveIndex = 0; waveIndex < maxWaves; waveIndex++) {
      const waveData = { wave: maxWaves - waveIndex }; // Reverse order for chronological display
      
      selectedSegments.forEach(segmentKey => {
        const waves = gridData[segmentKey] || [];
        const wave = waves[waveIndex];
        if (wave) {
          waveData[segmentKey] = wave[selectedMetric] || 0;
          waveData[`${segmentKey}_date`] = wave.surveyCompleteDate?.toLocaleDateString() || '';
          waveData[`${segmentKey}_weeks`] = wave.weeksUntilRelease || 0;
        }
      });
      
      if (Object.keys(waveData).length > 1) {
        trendData.push(waveData);
      }
    }

    const toggleSegment = (segmentKey) => {
      setSelectedSegments(prev => 
        prev.includes(segmentKey) 
          ? prev.filter(key => key !== segmentKey)
          : [...prev, segmentKey]
      );
    };

    return (
      <Box>
        {/* Trend Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Metric to Trend</InputLabel>
              <Select
                value={selectedMetric}
                label="Metric to Trend"
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                {metricOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Segments to Compare:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {segments.map(segment => (
                  <Chip
                    key={segment.key}
                    label={segment.label}
                    onClick={() => toggleSegment(segment.key)}
                    variant={selectedSegments.includes(segment.key) ? 'filled' : 'outlined'}
                    sx={{
                      backgroundColor: selectedSegments.includes(segment.key) ? segment.color + '20' : 'transparent',
                      color: selectedSegments.includes(segment.key) ? segment.color : 'text.secondary',
                      borderColor: segment.color,
                      fontWeight: selectedSegments.includes(segment.key) ? 600 : 400,
                      '&:hover': { backgroundColor: segment.color + '30' }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Trend Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
              {metricOptions.find(opt => opt.value === selectedMetric)?.label} Trends Across Survey Waves
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={trendData.reverse()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="wave" 
                  label={{ value: 'Survey Wave (Chronological)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  domain={[0, 100]}
                  label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  formatter={(value, name) => {
                    const segment = segments.find(s => s.key === name);
                    return [`${value}%`, segment?.label || name];
                  }}
                  labelFormatter={(label) => `Survey Wave ${label}`}
                />
                {selectedSegments.map(segmentKey => {
                  const segment = segments.find(s => s.key === segmentKey);
                  return segment ? (
                    <Line
                      key={segmentKey}
                      type="monotone"
                      dataKey={segmentKey}
                      stroke={segment.color}
                      strokeWidth={3}
                      dot={{ fill: segment.color, strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, stroke: segment.color, strokeWidth: 2 }}
                      name={segment.label}
                    />
                  ) : null;
                })}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Trend Insights */}
        <Box sx={{ mt: 2 }}>
          <TrendInsights trendData={trendData} selectedMetric={selectedMetric} selectedSegments={selectedSegments} segments={segments} />
        </Box>
      </Box>
    );
  };

  // Trend Insights Component
  const TrendInsights = ({ trendData, selectedMetric, selectedSegments, segments }) => {
    const insights = [];

    if (trendData.length >= 2 && selectedSegments.length >= 2) {
      const firstWave = trendData[0];
      const lastWave = trendData[trendData.length - 1];

      selectedSegments.forEach(segmentKey => {
        const segment = segments.find(s => s.key === segmentKey);
        const firstValue = firstWave[segmentKey] || 0;
        const lastValue = lastWave[segmentKey] || 0;
        const change = lastValue - firstValue;
        
        if (Math.abs(change) > 5) {
          insights.push({
            segment: segment.label,
            change,
            color: segment.color,
            trend: change > 0 ? 'increased' : 'decreased'
          });
        }
      });
    }

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {insights.map((insight, index) => (
          <Chip
            key={index}
            label={`${insight.segment} ${insight.trend} ${Math.abs(insight.change).toFixed(0)}pp`}
            icon={insight.change > 0 ? <TrendingUp /> : <TrendingDown />}
            sx={{
              backgroundColor: insight.color + '20',
              color: insight.color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: insight.change > 0 ? '#4caf50' : '#f44336' }
            }}
          />
        ))}
        {insights.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            Select multiple segments and metrics to see trend insights
          </Typography>
        )}
      </Box>
    );
  };

  // Marketing Intelligence Tab Component
  const MarketingIntelligenceTab = ({ surveyWaves, gridData }) => {
    const [budgetAmount, setBudgetAmount] = useState(1000000); // $1M default
    const [campaignDuration, setCampaignDuration] = useState(12); // 12 weeks default
    const [selectedScenario, setSelectedScenario] = useState('balanced');

    if (!surveyWaves || surveyWaves.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Marketing Intelligence requires survey data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete survey data will enable predictive marketing analytics
          </Typography>
        </Paper>
      );
    }

    const latestWave = getLatestWave(surveyWaves);
    
    return (
      <Box>
        {/* Marketing Intelligence Header */}
        <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${brandColors.primary}15 0%, ${brandColors.secondary}15 100%)` }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.primary, mb: 1 }}>
            🎯 Marketing Intelligence Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            AI-Powered Campaign Strategy & Box Office Predictions
          </Typography>
          
          {latestWave && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Chip 
                label={`${latestWave.pctAware}% Current Awareness`} 
                color="primary" 
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                label={`${latestWave.pctInterestNotAware}% Untapped Interest`} 
                color="secondary" 
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                label={`${latestWave.weeksUntilRelease} weeks to release`} 
                color="info" 
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
        </Paper>

        {/* Top Row: Opportunity Scoring + Campaign Strategy + Box Office */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <MarketingOpportunityScoring latestWave={latestWave} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CampaignStrategyMatrix latestWave={latestWave} />
          </Grid>
          <Grid item xs={12} md={4}>
            <BoxOfficePredictor 
              latestWave={latestWave} 
              budgetAmount={budgetAmount}
              setBudgetAmount={setBudgetAmount}
            />
          </Grid>
        </Grid>

        {/* Middle Row: Budget Allocator */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <MarketingBudgetAllocator 
              latestWave={latestWave} 
              budgetAmount={budgetAmount}
              campaignDuration={campaignDuration}
              setCampaignDuration={setCampaignDuration}
              selectedScenario={selectedScenario}
              setSelectedScenario={setSelectedScenario}
            />
          </Grid>
        </Grid>

        {/* Bottom Row: Release Timing Optimizer */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ReleaseTimingOptimizer surveyWaves={surveyWaves} />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Marketing Opportunity Scoring Component
  const MarketingOpportunityScoring = ({ latestWave }) => {
    if (!latestWave) return <Card><CardContent><Typography>No data available</Typography></CardContent></Card>;

    const calculateOpportunityScore = () => {
      const awareness = latestWave.pctAware || 0;
      const interestNotAware = latestWave.pctInterestNotAware || 0;
      const interestOfAware = latestWave.pctInterestOfAware || 0;
      
      // Debug logging
      console.log('🔍 Marketing Opportunity Debug:', {
        awareness,
        interestNotAware, 
        interestOfAware,
        latestWave
      });
      
      // Awareness opportunity (untapped market) 
      // People who would be interested if they were aware
      const awarenessGap = 100 - awareness;
      const awarenessOpportunity = (interestNotAware * awarenessGap) / 100;
      
      // Conversion opportunity (aware but not interested)
      // People who are aware but could be converted to interested
      const conversionGap = 100 - interestOfAware;
      const conversionOpportunity = (awareness * conversionGap) / 10000; // Fix: divide by 10000 since both are percentages
      
      // Total market potential
      // Current interested + potential from awareness campaigns
      const currentInterestedMarket = (awareness * interestOfAware) / 100;
      const potentialFromAwareness = awarenessOpportunity;
      const totalPotential = currentInterestedMarket + potentialFromAwareness;
      
      const result = {
        awarenessOpportunity: Math.round(awarenessOpportunity),
        conversionOpportunity: Math.round(conversionOpportunity), 
        totalPotential: Math.round(totalPotential),
        opportunityRank: awarenessOpportunity > conversionOpportunity ? 'Awareness-Driven' : 'Conversion-Driven'
      };
      
      console.log('📊 Calculated Opportunities:', result);
      return result;
    };

    const scores = calculateOpportunityScore();

    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
            🎪 Opportunity Scoring
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.primary }}>
              {scores.totalPotential}%
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Total Market Potential
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {scores.awarenessOpportunity}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Awareness Opportunity
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#fce4ec', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#c2185b' }}>
                  {scores.conversionOpportunity}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Conversion Opportunity
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            icon={<TrendingUp />}
          >
            <Typography variant="body2">
              <strong>Strategy:</strong> {scores.opportunityRank} campaign recommended
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  };

  // Campaign Strategy Matrix Component
  const CampaignStrategyMatrix = ({ latestWave }) => {
    if (!latestWave) return <Card><CardContent><Typography>No data available</Typography></CardContent></Card>;

    const getRecommendedCampaign = () => {
      const awareness = latestWave.pctAware || 0;
      const interestNotAware = latestWave.pctInterestNotAware || 0;
      const interestOfAware = latestWave.pctInterestOfAware || 0;
      const theater = latestWave.pctTheater || 0;

      if (interestNotAware > 30 && awareness < 50) {
        return {
          type: 'Awareness Blitz',
          icon: <Visibility />,
          color: '#1976d2',
          tactics: ['Social Media', 'Influencer Marketing', 'Viral Content', 'PR Campaigns'],
          timing: '8-12 weeks before release',
          budget: '40-50% of total budget',
          expectedROI: '3:1 to 5:1'
        };
      } else if (awareness > 50 && interestOfAware < 60) {
        return {
          type: 'Conversion Focus',
          icon: <Favorite />,
          color: '#c2185b', 
          tactics: ['Trailer Optimization', 'Review Campaigns', 'Star Appearances', 'Behind-the-Scenes'],
          timing: '4-8 weeks before release',
          budget: '30-40% of total budget',
          expectedROI: '2:1 to 4:1'
        };
      } else if (interestOfAware > 60 && theater > 40) {
        return {
          type: 'Harvest Mode',
          icon: <Movie />,
          color: '#388e3c',
          tactics: ['Ticket Pre-sales', 'Premium Format Push', 'Opening Weekend Blitz'],
          timing: '2-4 weeks before release',
          budget: '20-30% of total budget', 
          expectedROI: '1.5:1 to 3:1'
        };
      } else {
        return {
          type: 'Balanced Approach',
          icon: <TrendingUp />,
          color: '#ff9800',
          tactics: ['Multi-Channel Mix', 'Phased Campaign', 'Test & Learn'],
          timing: '6-10 weeks before release',
          budget: '35% of total budget',
          expectedROI: '2:1 to 3.5:1'
        };
      }
    };

    const campaign = getRecommendedCampaign();

    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
            📊 Campaign Strategy
          </Typography>
          
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 2, 
              backgroundColor: campaign.color + '20',
              borderRadius: 3,
              mb: 2
            }}>
              {campaign.icon}
              <Typography variant="h6" sx={{ fontWeight: 700, color: campaign.color }}>
                {campaign.type}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              🎯 Recommended Tactics:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {campaign.tactics.map(tactic => (
                <Chip 
                  key={tactic}
                  label={tactic} 
                  size="small"
                  sx={{ 
                    backgroundColor: campaign.color + '15',
                    color: campaign.color,
                    fontSize: '0.7rem'
                  }}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ fontSize: '0.8rem' }}>
            <Grid item xs={12}>
              <Typography variant="caption" display="block">
                ⏰ <strong>Timing:</strong> {campaign.timing}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" display="block">
                💰 <strong>Budget:</strong> {campaign.budget}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" display="block">
                📈 <strong>Expected ROI:</strong> {campaign.expectedROI}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Box Office Predictor Component
  const BoxOfficePredictor = ({ latestWave, budgetAmount, setBudgetAmount }) => {
    const [marketingLift, setMarketingLift] = useState(15);

    const calculateBoxOffice = (marketingLift = 0) => {
      if (!latestWave) return { baseRevenue: 0, liftedRevenue: 0, uplift: 0, roi: 0 };
      
      const baseAwareness = latestWave.pctAware || 0;
      const interestOfAware = latestWave.pctInterestOfAware || 0;
      const theaterIntent = latestWave.pctTheater || 0;
      
      // Simplified box office model
      const liftedAwareness = Math.min(100, baseAwareness + marketingLift);
      const conversionRate = (interestOfAware / 100) * (theaterIntent / 100);
      const marketSize = 50000000; // 50M potential audience (US)
      const avgTicketPrice = 12;
      
      const baseRevenue = marketSize * (baseAwareness / 100) * conversionRate * avgTicketPrice;
      const liftedRevenue = marketSize * (liftedAwareness / 100) * conversionRate * avgTicketPrice;
      
      return {
        baseRevenue: Math.round(baseRevenue),
        liftedRevenue: Math.round(liftedRevenue),
        uplift: Math.round(liftedRevenue - baseRevenue),
        roi: budgetAmount > 0 ? ((liftedRevenue - baseRevenue) / budgetAmount).toFixed(2) : 0
      };
    };

    if (!latestWave) return <Card><CardContent><Typography>No data available</Typography></CardContent></Card>;

    const projection = calculateBoxOffice(marketingLift);

    return (
      <Card sx={{ height: 350 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            💰 Box Office Predictor
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Marketing Budget</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(Number(e.target.value))}
              InputProps={{
                startAdornment: <Typography variant="body2">$</Typography>
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">Awareness Lift: {marketingLift}%</Typography>
            <Slider
              value={marketingLift}
              onChange={(e, value) => setMarketingLift(value)}
              min={0}
              max={50}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' }
              ]}
            />
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.primary }}>
              ${(projection.liftedRevenue / 1000000).toFixed(1)}M
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Projected Revenue
            </Typography>
          </Box>

          <Grid container spacing={1} sx={{ fontSize: '0.8rem' }}>
            <Grid item xs={6}>
              <Typography variant="caption" display="block">
                📊 <strong>Base:</strong> ${(projection.baseRevenue / 1000000).toFixed(1)}M
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" display="block">
                📈 <strong>Uplift:</strong> ${(projection.uplift / 1000000).toFixed(1)}M
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" display="block">
                💎 <strong>Marketing ROI:</strong> {projection.roi}:1
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Marketing Budget Allocator Component
  const MarketingBudgetAllocator = ({ latestWave, budgetAmount, campaignDuration, setCampaignDuration, selectedScenario, setSelectedScenario }) => {
    if (!latestWave) return <Card><CardContent><Typography>No data available</Typography></CardContent></Card>;

    const calculateBudgetAllocation = () => {
      const awareness = latestWave.pctAware || 0;
      const interestNotAware = latestWave.pctInterestNotAware || 0;
      const interestOfAware = latestWave.pctInterestOfAware || 0;

      let allocation = {};

      if (selectedScenario === 'awareness-focused') {
        allocation = {
          social: 30,
          influencer: 25,
          pr: 20,
          digital: 15,
          traditional: 10
        };
      } else if (selectedScenario === 'conversion-focused') {
        allocation = {
          digital: 35,
          traditional: 25,
          social: 20,
          pr: 15,
          influencer: 5
        };
      } else { // balanced
        allocation = {
          digital: 30,
          social: 25,
          traditional: 20,
          pr: 15,
          influencer: 10
        };
      }

      return Object.entries(allocation).map(([channel, percentage]) => ({
        channel: channel.charAt(0).toUpperCase() + channel.slice(1),
        percentage,
        amount: Math.round((budgetAmount * percentage) / 100),
        expectedROI: getChannelROI(channel, latestWave)
      }));
    };

    const getChannelROI = (channel, wave) => {
      // Calculate ROI based on actual film performance metrics
      const awareness = wave.pctAware || 0;
      const interestNotAware = wave.pctInterestNotAware || 0;
      const interestOfAware = wave.pctInterestOfAware || 0;
      const theaterIntent = wave.pctTheater || 0;
      
      // Base efficiency scores for each channel type
      const channelEfficiency = {
        social: 0.8, // Good for awareness building
        influencer: 0.7, // Good for targeted awareness  
        digital: 0.9, // Good for conversion
        pr: 0.6, // Good for credibility
        traditional: 0.5 // Broad reach but less targeted
      };
      
      // Calculate opportunity-based ROI
      let opportunityScore = 0;
      
      if (channel === 'social' || channel === 'influencer') {
        // Awareness-building channels: higher ROI when high Interest Not Aware
        opportunityScore = interestNotAware * (100 - awareness) / 100;
      } else if (channel === 'digital') {
        // Conversion channel: higher ROI when high awareness but low interest
        opportunityScore = awareness * (100 - interestOfAware) / 100;
      } else if (channel === 'traditional') {
        // Broad reach: average of all opportunities
        opportunityScore = (awareness + interestNotAware + theaterIntent) / 3;
      } else { // PR
        // Credibility channel: works best with moderate awareness
        opportunityScore = Math.min(awareness, 60) + (interestOfAware / 2);
      }
      
      // Calculate ROI: (Opportunity Score × Channel Efficiency) / 10
      const calculatedROI = (opportunityScore * channelEfficiency[channel]) / 10;
      
      // Ensure minimum ROI of 1.5 and maximum of 6.0
      const finalROI = Math.max(1.5, Math.min(6.0, calculatedROI));
      
      console.log(`📊 ${channel} ROI Calculation:`, {
        awareness,
        interestNotAware,
        interestOfAware,
        opportunityScore,
        efficiency: channelEfficiency[channel],
        calculatedROI,
        finalROI
      });
      
      return finalROI.toFixed(1);
    };

    const budgetBreakdown = calculateBudgetAllocation();
    const totalROI = budgetBreakdown.reduce((sum, item) => sum + (item.expectedROI * item.percentage / 100), 0).toFixed(1);

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🤖 AI Marketing Budget Allocator
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Campaign Strategy</InputLabel>
                <Select
                  value={selectedScenario}
                  label="Campaign Strategy"
                  onChange={(e) => setSelectedScenario(e.target.value)}
                >
                  <MenuItem value="awareness-focused">Awareness-Focused</MenuItem>
                  <MenuItem value="conversion-focused">Conversion-Focused</MenuItem>
                  <MenuItem value="balanced">Balanced Approach</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Duration (weeks)"
                type="number"
                value={campaignDuration}
                onChange={(e) => setCampaignDuration(Number(e.target.value))}
                sx={{ width: 120 }}
              />
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                💰 Budget Distribution - ${(budgetAmount / 1000000).toFixed(1)}M Total
              </Typography>
              <Grid container spacing={2}>
                {budgetBreakdown.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.channel}>
                    <Box sx={{ 
                      p: 2, 
                      border: '2px solid',
                      borderColor: brandColors.primary + '30',
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.primary }}>
                        {item.percentage}%
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.channel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${(item.amount / 1000).toFixed(0)}K
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#4caf50', fontWeight: 600 }}>
                        ROI: {item.expectedROI}:1
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 3, 
                backgroundColor: brandColors.primary + '10',
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.primary, mb: 1 }}>
                  {totalROI}:1
                </Typography>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Weighted Average ROI
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Campaign Timeline: {campaignDuration} weeks
                </Typography>
                
                <Alert severity="success" sx={{ textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Recommendation:</strong> {selectedScenario === 'awareness-focused' ? 
                      'Focus on social and influencer channels for maximum reach' : 
                      selectedScenario === 'conversion-focused' ?
                      'Prioritize digital and traditional for conversion' :
                      'Balanced mix for steady growth'
                    }
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Release Timing Optimizer Component
  const ReleaseTimingOptimizer = ({ surveyWaves }) => {
    if (!surveyWaves || surveyWaves.length < 2) {
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              ⏰ Release Timing Optimizer
            </Typography>
            <Typography color="text.secondary">
              Multiple survey waves required for timing analysis
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const analyzeMomentum = () => {
      const sortedWaves = [...surveyWaves].sort((a, b) => new Date(a.surveyCompleteDate) - new Date(b.surveyCompleteDate));
      
      const momentum = sortedWaves.map((wave, index) => {
        const prevWave = sortedWaves[index - 1];
        let trend = 'stable';
        let change = 0;
        
        if (prevWave) {
          const currentScore = (wave.pctAware + wave.pctInterest + wave.pctTheater) / 3;
          const prevScore = (prevWave.pctAware + prevWave.pctInterest + prevWave.pctTheater) / 3;
          change = currentScore - prevScore;
          
          if (change > 5) trend = 'rising';
          else if (change < -5) trend = 'declining';
        }
        
        return {
          ...wave,
          overallScore: (wave.pctAware + wave.pctInterest + wave.pctTheater) / 3,
          trend,
          change: Math.round(change),
          weeksOut: wave.weeksUntilRelease
        };
      });
      
      return momentum;
    };

    const getOptimalWindow = (momentum) => {
      // Find peak momentum period
      const peakMomentum = momentum.reduce((max, current) => 
        current.overallScore > max.overallScore ? current : max
      );
      
      const currentWeeksOut = peakMomentum.weeksUntilRelease;
      
      if (currentWeeksOut > 8) {
        return {
          recommendation: 'Hold Current Date',
          reasoning: 'Momentum is building - maintain current release schedule',
          confidence: 'High',
          color: '#4caf50'
        };
      } else if (currentWeeksOut < 4 && peakMomentum.trend === 'declining') {
        return {
          recommendation: 'Consider Earlier Release',
          reasoning: 'Momentum declining - consider moving up 2-3 weeks',
          confidence: 'Medium',
          color: '#ff9800'
        };
      } else if (peakMomentum.trend === 'rising') {
        return {
          recommendation: 'Monitor Closely',
          reasoning: 'Rising momentum - may benefit from slight delay',
          confidence: 'Medium',
          color: '#2196f3'
        };
      } else {
        return {
          recommendation: 'Maintain Schedule',
          reasoning: 'Stable momentum - current timing appears optimal',
          confidence: 'High',
          color: '#4caf50'
        };
      }
    };

    const momentum = analyzeMomentum();
    const optimal = getOptimalWindow(momentum);

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            ⏰ Release Timing Optimizer
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                📈 Momentum Analysis Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={momentum}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="weeksOut" label={{ value: 'Weeks Until Release', position: 'insideBottom', offset: -5 }} />
                  <YAxis domain={[0, 100]} label={{ value: 'Overall Score', angle: -90, position: 'insideLeft' }} />
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value.toFixed(1)}%`, 'Overall Score']}
                    labelFormatter={(label) => `${label} weeks until release`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="overallScore" 
                    stroke={brandColors.primary} 
                    strokeWidth={3}
                    dot={{ fill: brandColors.primary, strokeWidth: 2, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 3, 
                backgroundColor: optimal.color + '20',
                borderRadius: 2,
                border: `2px solid ${optimal.color}`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: optimal.color, mb: 2 }}>
                  {optimal.recommendation}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {optimal.reasoning}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block', 
                  fontWeight: 600,
                  color: optimal.color 
                }}>
                  Confidence: {optimal.confidence}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  📊 Current Metrics:
                </Typography>
                {momentum.length > 0 && (
                  <Box sx={{ fontSize: '0.85rem' }}>
                    <Typography variant="body2">
                      • Overall Score: {momentum[momentum.length - 1].overallScore.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2">
                      • Trend: {momentum[momentum.length - 1].trend}
                    </Typography>
                    <Typography variant="body2">
                      • Weeks Out: {momentum[momentum.length - 1].weeksOut}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // ========================================
  // TAB 5: CREATIVE OPTIMIZATION - REMOVED 2025-10-13
  // ========================================
  // See REMOVED_SECTIONS.md for details and restoration instructions

  // Component removed - see REMOVED_SECTIONS.md for restoration

  const MessageTestingResults = ({ latestWave }) => {
    const getMessagePerformance = () => {
      const awareness = latestWave.pctAware || 0;
      const interest = latestWave.pctInterest || 0;
      const theater = latestWave.pctTheater || 0;
      
      // Simulate message testing results based on performance
      const messages = [
        {
          concept: 'Action-Packed Thriller',
          performance: Math.min(95, theater + 10),
          audience: 'Male Under 35',
          recommendation: theater > 50 ? 'Primary Message' : 'Secondary Message'
        },
        {
          concept: 'Emotional Journey',
          performance: Math.min(95, interest + 5),
          audience: 'Female Over 35', 
          recommendation: interest > 60 ? 'Primary Message' : 'Test Further'
        },
        {
          concept: 'Star Power',
          performance: Math.min(95, awareness + 15),
          audience: 'All Demographics',
          recommendation: awareness < 50 ? 'Primary Message' : 'Support Message'
        }
      ];
      
      return messages.sort((a, b) => b.performance - a.performance);
    };

    const messages = getMessagePerformance();

    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            📊 Message Testing Results
          </Typography>
          
          {messages.map((message, index) => (
            <Box key={message.concept} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {message.concept}
                </Typography>
                <Chip 
                  label={`${message.performance}% Score`}
                  color={message.performance > 70 ? 'success' : message.performance > 50 ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Best for: {message.audience}
              </Typography>
              
              <Alert 
                severity={message.recommendation === 'Primary Message' ? 'success' : 'info'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>Recommendation:</strong> {message.recommendation}
                </Typography>
              </Alert>
              
              <LinearProgress 
                variant="determinate" 
                value={message.performance} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const CreativeRecommendations = ({ latestWave, imdbData }) => {
    const getCreativeActions = () => {
      const awareness = latestWave.pctAware || 0;
      const interest = latestWave.pctInterest || 0;
      const theater = latestWave.pctTheater || 0;
      const genre = imdbData?.genres?.[0] || 'Drama';

      const actions = [];
      
      if (awareness < 40) {
        actions.push({
          priority: 'HIGH',
          action: 'Create 15-second social teasers',
          reason: 'Low awareness needs quick, shareable content',
          timeline: 'This week',
          cost: '$25K'
        });
      }
      
      if (interest < 50) {
        actions.push({
          priority: 'HIGH',
          action: 'Produce behind-the-scenes content',
          reason: 'Build emotional connection to drive interest',
          timeline: '2 weeks',
          cost: '$40K'
        });
      }
      
      if (theater < 60) {
        actions.push({
          priority: 'MEDIUM',
          action: 'Create premium format spots',
          reason: 'Theater intent needs big-screen emphasis',
          timeline: '3 weeks',
          cost: '$60K'
        });
      }

      actions.push({
        priority: 'LOW',
        action: `Develop ${genre}-specific trailer cuts`,
        reason: 'Genre targeting for niche audiences',
        timeline: '4 weeks',
        cost: '$35K'
      });

      return actions;
    };

    const actions = getCreativeActions();

    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            🎨 Creative Action Items
          </Typography>
          
          {actions.map((action, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip 
                  label={action.priority}
                  size="small"
                  color={action.priority === 'HIGH' ? 'error' : action.priority === 'MEDIUM' ? 'warning' : 'default'}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {action.action}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {action.reason}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, fontSize: '0.8rem' }}>
                <Typography variant="caption">
                  ⏰ {action.timeline}
                </Typography>
                <Typography variant="caption">
                  💰 {action.cost}
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const TalentActivationStrategy = ({ latestWave, imdbData }) => {
    const getTalentStrategy = () => {
      const cast = imdbData?.cast?.slice(0, 4) || [
        { name: 'Lead Actor', character: 'Protagonist' },
        { name: 'Supporting Actor', character: 'Antagonist' }
      ];

      return cast.map((actor, index) => ({
        name: actor.name,
        character: actor.character,
        demographics: index === 0 ? 'All Audiences' : 
                     index === 1 ? 'Male 18-34' : 
                     index === 2 ? 'Female 25-54' : 'All Ages',
        channels: index === 0 ? ['Late Night TV', 'Podcasts', 'Social'] :
                  index === 1 ? ['Gaming', 'Sports', 'Social'] :
                  index === 2 ? ['Lifestyle', 'Talk Shows'] : ['Family Shows'],
        priority: index === 0 ? 'Primary' : index === 1 ? 'Secondary' : 'Support',
        bookings: index === 0 ? 8 : index === 1 ? 4 : 2
      }));
    };

    const strategy = getTalentStrategy();

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            🎭 Talent Activation Strategy
          </Typography>
          
          <Grid container spacing={3}>
            {strategy.map((talent, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ 
                  p: 2, 
                  border: '2px solid',
                  borderColor: talent.priority === 'Primary' ? brandColors.primary + '60' : '#e0e0e0',
                  borderRadius: 2 
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {talent.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {talent.character} • {talent.priority} Focus
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Target:</strong> {talent.demographics}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Channels:</strong> {talent.channels.join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Bookings:</strong> {talent.bookings} appearances
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const AssetPriorityMatrix = ({ latestWave }) => {
    const getAssetPriority = () => {
      const awareness = latestWave.pctAware || 0;
      const interest = latestWave.pctInterest || 0;
      const theater = latestWave.pctTheater || 0;

      const assets = [
        {
          asset: 'Main Trailer (60s)',
          urgency: awareness < 50 ? 'URGENT' : 'HIGH',
          impact: 'High',
          cost: '$150K',
          timeline: '1 week',
          roi: awareness < 50 ? '4.2x' : '2.8x'
        },
        {
          asset: 'Social Cut-Downs (15s)',
          urgency: 'HIGH',
          impact: 'Medium',
          cost: '$25K',
          timeline: '3 days',
          roi: '3.5x'
        },
        {
          asset: 'TV Spots (30s)',
          urgency: theater < 60 ? 'HIGH' : 'MEDIUM',
          impact: 'High',
          cost: '$80K',
          timeline: '1 week',
          roi: '3.1x'
        },
        {
          asset: 'Character Posters',
          urgency: 'MEDIUM',
          impact: 'Medium',
          cost: '$15K',
          timeline: '2 days',
          roi: '2.4x'
        },
        {
          asset: 'Behind-the-Scenes',
          urgency: interest < 60 ? 'HIGH' : 'LOW',
          impact: 'Medium',
          cost: '$45K',
          timeline: '1 week',
          roi: '2.9x'
        }
      ];

      return assets.sort((a, b) => {
        const urgencyOrder = { 'URGENT': 3, 'HIGH': 2, 'MEDIUM': 1, 'LOW': 0 };
        return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
      });
    };

    const assets = getAssetPriority();

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            📋 Asset Production Priority Matrix
          </Typography>
          
          <Grid container spacing={2}>
            {assets.map((asset, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Box sx={{ 
                  p: 2, 
                  border: '2px solid',
                  borderColor: asset.urgency === 'URGENT' ? '#f44336' :
                              asset.urgency === 'HIGH' ? '#ff9800' :
                              asset.urgency === 'MEDIUM' ? '#2196f3' : '#9e9e9e',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {asset.asset}
                    </Typography>
                    <Chip 
                      label={asset.urgency}
                      size="small"
                      color={asset.urgency === 'URGENT' ? 'error' : 
                             asset.urgency === 'HIGH' ? 'warning' : 
                             asset.urgency === 'MEDIUM' ? 'info' : 'default'}
                    />
                  </Box>
                  
                  <Grid container spacing={1} sx={{ fontSize: '0.8rem' }}>
                    <Grid item xs={6}>
                      <Typography variant="caption">Impact: {asset.impact}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption">Cost: {asset.cost}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption">Timeline: {asset.timeline}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption">ROI: {asset.roi}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // ========================================
  // TAB 6: AUDIENCE ACTIVATION PLAN - REMOVED 2025-10-13
  // ========================================
  // See REMOVED_SECTIONS.md for details and restoration instructions

  // Component removed - see REMOVED_SECTIONS.md for restoration

  const SegmentPrioritization = ({ latestWave }) => {
    const getSegmentPriority = () => {
      const segments = [
        {
          name: 'Male Under 35',
          potential: latestWave.pctInterestNotAware || 0,
          current: latestWave.pctAware || 0,
          conversion: latestWave.pctTheater || 0,
          priority: 'HIGH'
        },
        {
          name: 'Female Under 35', 
          potential: (latestWave.pctInterestNotAware || 0) * 0.9,
          current: (latestWave.pctAware || 0) * 1.1,
          conversion: (latestWave.pctTheater || 0) * 0.95,
          priority: 'HIGH'
        },
        {
          name: 'Male Over 35',
          potential: (latestWave.pctInterestNotAware || 0) * 0.7,
          current: (latestWave.pctAware || 0) * 1.2,
          conversion: (latestWave.pctTheater || 0) * 1.1,
          priority: 'MEDIUM'
        },
        {
          name: 'Female Over 35',
          potential: (latestWave.pctInterestNotAware || 0) * 0.8,
          current: (latestWave.pctAware || 0) * 0.9,
          conversion: (latestWave.pctTheater || 0) * 1.05,
          priority: 'MEDIUM'
        }
      ];

      return segments.map(segment => ({
        ...segment,
        score: (segment.potential * 0.4) + (segment.conversion * 0.6)
      })).sort((a, b) => b.score - a.score);
    };

    const segments = getSegmentPriority();

    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            🎯 Segment Priority Ranking
          </Typography>
          
          {segments.map((segment, index) => (
            <Box key={segment.name} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  #{index + 1} {segment.name}
                </Typography>
                <Chip 
                  label={segment.priority}
                  size="small"
                  color={segment.priority === 'HIGH' ? 'error' : 'warning'}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Opportunity Score: {Math.round(segment.score)}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(100, segment.score)} 
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              
              <Grid container spacing={2} sx={{ fontSize: '0.8rem' }}>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    Potential: {Math.round(segment.potential)}%
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    Current: {Math.round(segment.current)}%
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    Theater: {Math.round(segment.conversion)}%
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const ActivationPlaybooks = ({ latestWave }) => {
    const getPlaybooks = () => {
      const awareness = latestWave.pctAware || 0;
      const interest = latestWave.pctInterest || 0;

      const playbooks = [
        {
          segment: 'Male Under 35',
          tactics: [
            'Launch TikTok challenge campaign',
            'Partner with gaming influencers',
            'Sponsor esports events',
            'Create Snapchat AR filters'
          ],
          budget: '$150K',
          timeline: '4 weeks'
        },
        {
          segment: 'Female Under 35',
          tactics: [
            'Instagram beauty/lifestyle partnerships',
            'YouTube creator collaborations', 
            'Pinterest mood board campaigns',
            'Spotify playlist placements'
          ],
          budget: '$120K',
          timeline: '3 weeks'
        },
        {
          segment: 'Male Over 35',
          tactics: [
            'Podcast advertising (Joe Rogan, etc)',
            'Sports partnership activations',
            'LinkedIn sponsored content',
            'Traditional radio spots'
          ],
          budget: '$200K',
          timeline: '6 weeks'
        },
        {
          segment: 'Female Over 35',
          tactics: [
            'Facebook community engagement',
            'Lifestyle magazine partnerships',
            'Morning show appearances',
            'Book club/reading group outreach'
          ],
          budget: '$180K',
          timeline: '5 weeks'
        }
      ];

      return playbooks;
    };

    const playbooks = getPlaybooks();

    return (
      <Card sx={{ height: 400, overflowY: 'auto' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            📋 Activation Playbooks
          </Typography>
          
          {playbooks.map((playbook, index) => (
            <Box key={playbook.segment} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {playbook.segment}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                {playbook.tactics.map((tactic, tacticIndex) => (
                  <Typography key={tacticIndex} variant="body2" sx={{ mb: 0.5 }}>
                    • {tactic}
                  </Typography>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, fontSize: '0.8rem' }}>
                <Typography variant="caption">
                  💰 {playbook.budget}
                </Typography>
                <Typography variant="caption">
                  ⏰ {playbook.timeline}
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const ChannelMapping = ({ latestWave }) => {
    const getChannelMap = () => {
      return [
        {
          channel: 'TikTok',
          demographics: ['Male Under 35', 'Female Under 35'],
          reach: '2.1M',
          engagement: '8.4%',
          cost: '$2.50 CPM',
          recommendation: 'Primary for Gen Z awareness'
        },
        {
          channel: 'Instagram',
          demographics: ['Female Under 35', 'Female Over 35'],
          reach: '1.8M',
          engagement: '6.2%',
          cost: '$3.20 CPM',
          recommendation: 'Strong visual storytelling platform'
        },
        {
          channel: 'YouTube',
          demographics: ['Male Under 35', 'Male Over 35'],
          reach: '3.5M',
          engagement: '4.1%',
          cost: '$4.80 CPM',
          recommendation: 'Long-form content and trailers'
        },
        {
          channel: 'Facebook',
          demographics: ['Female Over 35', 'Male Over 35'],
          reach: '4.2M',
          engagement: '3.8%',
          cost: '$2.90 CPM',
          recommendation: 'Mature audience engagement'
        },
        {
          channel: 'Podcasts',
          demographics: ['Male Over 35'],
          reach: '850K',
          engagement: '12.1%',
          cost: '$15.00 CPM',
          recommendation: 'High-engagement niche targeting'
        }
      ];
    };

    const channels = getChannelMap();

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            📺 Channel Mapping & Performance
          </Typography>
          
          <Grid container spacing={2}>
            {channels.map((channel, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Box sx={{ 
                  p: 2, 
                  border: '2px solid',
                  borderColor: brandColors.primary + '30',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: brandColors.primary }}>
                    {channel.channel}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Demographics:</strong> {channel.demographics.join(', ')}
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mb: 2, fontSize: '0.8rem' }}>
                    <Grid item xs={6}>
                      <Typography variant="caption">Reach: {channel.reach}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption">Engagement: {channel.engagement}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption">Cost: {channel.cost}</Typography>
                    </Grid>
                  </Grid>
                  
                  <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                    {channel.recommendation}
                  </Alert>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const InfluencerRecommendations = ({ latestWave }) => {
    const getInfluencerRecs = () => {
      return [
        {
          category: 'Gaming/Tech',
          target: 'Male Under 35',
          influencers: ['MrBeast', 'PewDiePie', 'Ninja'],
          reach: '50M+',
          cost: '$100K',
          fit: 'High'
        },
        {
          category: 'Beauty/Lifestyle',
          target: 'Female Under 35',
          influencers: ['Emma Chamberlain', 'James Charles', 'Addison Rae'],
          reach: '35M+',
          cost: '$80K',
          fit: 'High'
        },
        {
          category: 'Comedy/Entertainment',
          target: 'All Under 35',
          influencers: ['Ryan Reynolds', 'The Rock', 'Kevin Hart'],
          reach: '100M+',
          cost: '$200K',
          fit: 'Medium'
        },
        {
          category: 'Podcast Hosts',
          target: 'Male Over 35',
          influencers: ['Joe Rogan', 'Tim Ferriss', 'Gary Vaynerchuk'],
          reach: '20M+',
          cost: '$150K',
          fit: 'High'
        }
      ];
    };

    const influencers = getInfluencerRecs();

    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            🌟 Influencer Recommendations
          </Typography>
          
          {influencers.map((category, index) => (
            <Box key={category.category} sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {category.category}
                </Typography>
                <Chip 
                  label={`${category.fit} Fit`}
                  size="small"
                  color={category.fit === 'High' ? 'success' : 'warning'}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Target: {category.target}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Suggested:</strong> {category.influencers.join(', ')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, fontSize: '0.8rem' }}>
                <Typography variant="caption">
                  📊 {category.reach} reach
                </Typography>
                <Typography variant="caption">
                  💰 {category.cost} estimated
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const EventStrategy = ({ latestWave }) => {
    const getEventStrategy = () => {
      return [
        {
          event: 'Comic-Con Activation',
          target: 'Male Under 35',
          description: 'Interactive booth with VR experience',
          cost: '$300K',
          reach: '150K attendees',
          timing: '6 weeks before'
        },
        {
          event: 'Influencer Premiere',
          target: 'Female Under 35',
          description: 'Exclusive screening with top creators',
          cost: '$150K',
          reach: '50M social impressions',
          timing: '3 weeks before'
        },
        {
          event: 'Sports Partnership',
          target: 'Male Over 35',
          description: 'Stadium advertising during playoffs',
          cost: '$500K',
          reach: '20M viewers',
          timing: '4 weeks before'
        },
        {
          event: 'Lifestyle Festival',
          target: 'Female Over 35',
          description: 'Wellness/lifestyle event sponsorship',
          cost: '$200K',
          reach: '75K attendees',
          timing: '5 weeks before'
        }
      ];
    };

    const events = getEventStrategy();

    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            🎪 Event Strategy & Activations
          </Typography>
          
          {events.map((event, index) => (
            <Box key={event.event} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {event.event}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Target: {event.target}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                {event.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ fontSize: '0.8rem' }}>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    💰 {event.cost}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    📊 {event.reach}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption">
                    ⏰ {event.timing}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  // ========================================
  // ACTION & EXECUTION TABS - REMOVED 2025-10-13
  // ========================================
  // See REMOVED_SECTIONS.md for details and restoration instructions

  // Components removed - see REMOVED_SECTIONS.md for restoration

  // Get metric color
  const getMetricColor = (type) => {
    switch (type) {
      case 'awareness': return '#1976d2';
      case 'interest': return '#d32f2f';
      case 'theater': return '#388e3c';
      case 'pay': return '#f57c00';
      case 'heat': return '#ff5722';
      case 'plf': return '#9c27b0';
      default: return brandColors.primary;
    }
  };

  // Metric card component
  const MetricCard = ({ title, value, subtitle, icon, color, trend }) => (
    <Card sx={{ height: '100%', border: `2px solid ${color}20`, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ color, mr: 1 }}>{icon}</Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
            {value}%
          </Typography>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {trend > 0 ? <TrendingUp color="success" /> : 
               trend < 0 ? <TrendingDown color="error" /> : 
               <TrendingFlat color="disabled" />}
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(value, 100)}
          sx={{
            mt: 1,
            height: 6,
            borderRadius: 3,
            backgroundColor: `${color}20`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 3
            }
          }}
        />
      </CardContent>
    </Card>
  );

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button variant="contained" onClick={() => fetchData()} startIcon={<Refresh />}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  // No data state
  if (!latestWave) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          No survey data available for this film.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/film-tracking')} startIcon={<ArrowBack />}>
          Back to Film Tracking
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/film-tracking')} color="primary">
              <ArrowBack />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
              {/* Small Movie Poster */}
              {imdbData?.movie?.primary_image_url && (
                <Box sx={{ flexShrink: 0 }}>
                  <img
                    src={imdbData.movie.primary_image_url}
                    alt={latestWave.titleText || 'Film Poster'}
                    style={{
                      width: 80,
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  />
                </Box>
              )}
              
              {/* Film Title */}
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {latestWave.titleText || 'Unknown Film'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportDownloadButton 
              filmData={{ survey: { data: surveyWaves }, imdb: imdbData }} 
              size="small"
            />
            <IconButton onClick={() => fetchData()} color="primary" size="large">
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      </Box>


      {/* Enhanced Navigation - Card-Based Layout */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'stretch' }}>
          {/* Analysis & Intelligence Section */}
          <Paper 
            sx={{ 
              p: 3, 
              flex: '1 1 360px',
              background: `linear-gradient(135deg, ${brandColors.primary}08 0%, ${brandColors.primary}15 100%)`,
              border: `2px solid ${brandColors.primary}20`,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUp sx={{ color: brandColors.primary, mr: 1, fontSize: '1.2rem' }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: brandColors.primary, 
                  fontWeight: 700, 
                  fontSize: '0.9rem',
                  letterSpacing: '0.5px'
                }}
              >
                ANALYSIS & INTELLIGENCE
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Core analytics available in the Film Professional tier
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { index: 0, label: 'Survey Analytics', icon: <Timeline /> },
                { index: 1, label: 'Film Information', icon: <Movie /> },
                { index: 2, label: 'Film Comps', icon: <Handshake /> },
                { index: 3, label: 'Box Office Projections', icon: <AttachMoney /> }
              ].map(tab => (
                <Button
                  key={tab.index}
                  variant={activeTab === tab.index ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={tab.icon}
                  onClick={() => setActiveTab(tab.index)}
                  sx={{
                    minWidth: 140,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&.MuiButton-contained': {
                      backgroundColor: brandColors.primary,
                      color: 'white',
                      boxShadow: `0 4px 12px ${brandColors.primary}40`,
                      '&:hover': {
                        backgroundColor: brandColors.primary + 'dd',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${brandColors.primary}50`
                      }
                    },
                    '&.MuiButton-outlined': {
                      borderColor: brandColors.primary + '40',
                      color: brandColors.primary,
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        borderColor: brandColors.primary,
                        backgroundColor: brandColors.primary + '10',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${brandColors.primary}20`
                      }
                    }
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Paper>

        </Box>

        {/* Action & Execution Section - REMOVED 2025-10-13 */}
        {/* See REMOVED_SECTIONS.md for details and restoration instructions */}
      </Box>

      {/* Tab Content */}
      {/* Main Analysis Tabs - Professional Tier Required (Tabs 0-3) */}
      {(activeTab >= 0 && activeTab <= 3) && (
        canAccessFilmDetail ? (
          <>
      {activeTab === 0 && (
        <Box>
          {/* Filter Controls */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList sx={{ color: brandColors.primary }} />
                <Typography variant="h6" sx={{ color: brandColors.primary }}>
                  Demographic & Behavioral Filters
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetFilters}
                startIcon={<Refresh />}
              >
                Reset Filters
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Movie Frequency</InputLabel>
                  <Select
                    value={filters.movieFrequency}
                    label="Movie Frequency"
                    onChange={(e) => handleFilterChange('movieFrequency', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.movieFrequency.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Age Group</InputLabel>
                  <Select
                    value={filters.ageGroup}
                    label="Age Group"
                    onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.ageGroup.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={filters.gender}
                    label="Gender"
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.gender.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ethnicity</InputLabel>
                  <Select
                    value={filters.ethnicity}
                    label="Ethnicity"
                    onChange={(e) => handleFilterChange('ethnicity', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filterOptions.ethnicity.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <TextField
                  label="Survey Date From"
                  type="date"
                  value={filters.surveyDateFrom}
                  onChange={(e) => handleFilterChange('surveyDateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Latest Wave Metrics */}
          <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${brandColors.primary}10, ${brandColors.secondary}10)` }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: brandColors.primary }}>
              Latest Survey Results - {formatDateForDisplay(latestWave.surveyCompleteDate)}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Awareness"
                  value={latestWave.pctAware}
                  subtitle={`${latestWave.totalAware} of ${latestWave.totalResponses} respondents`}
                  icon={<Visibility />}
                  color={getMetricColor('awareness')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Interest"
                  value={latestWave.pctInterest}
                  subtitle={`${latestWave.totalInterest} want to see`}
                  icon={<Favorite />}
                  color={getMetricColor('interest')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Theater Intent"
                  value={latestWave.pctTheater}
                  subtitle={`${latestWave.totalTheater} prefer theaters`}
                  icon={<Movie />}
                  color={getMetricColor('theater')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Willing to Pay"
                  value={latestWave.pctWillingToPay}
                  subtitle={`${latestWave.totalWillingToPay} will pay`}
                  icon={<AttachMoney />}
                  color={getMetricColor('pay')}
                />
              </Grid>
            </Grid>

            {/* Secondary Metrics */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Heat Score"
                  value={latestWave.pctHeat}
                  subtitle={`${latestWave.totalHeat} high engagement`}
                  icon={<Whatshot />}
                  color={getMetricColor('heat')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Premium Format"
                  value={latestWave.pctPLF}
                  subtitle={`${latestWave.totalPLF} want PLF/IMAX`}
                  icon={<Star />}
                  color={getMetricColor('plf')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', border: `2px solid ${brandColors.info}20` }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Interest of Aware
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.info, mb: 1 }}>
                      {latestWave.pctInterestOfAware}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {latestWave.totalInterestOfAware} of {latestWave.totalAware} aware
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', border: `2px solid ${brandColors.warning}20` }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Release Timing
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: brandColors.warning, mb: 1 }}>
                      {latestWave.weeksUntilRelease}w
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {latestWave.timingDisplay}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Trend Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {['pctAware', 'pctInterest', 'pctWillingToPay', 'pctTheater'].map((metric, index) => {
              const trendData = getTrendData(transformedWaves, metric);
              const metricNames = {
                pctAware: 'Awareness',
                pctInterest: 'Interest', 
                pctWillingToPay: 'Willing to Pay',
                pctTheater: 'Theater Intent'
              };
              const metricColors = {
                pctAware: getMetricColor('awareness'),
                pctInterest: getMetricColor('interest'),
                pctWillingToPay: getMetricColor('pay'),
                pctTheater: getMetricColor('theater')
              };

              return (
                <Grid item xs={12} md={6} key={metric}>
                  <Card sx={{ height: 300 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {metricNames[metric]} Trend
                      </Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                          />
                          <YAxis 
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            label={{ value: '%', angle: -90, position: 'insideLeft' }}
                          />
                          <RechartsTooltip 
                            formatter={(value, name) => [`${value}%`, metricNames[metric]]}
                            labelFormatter={(label) => `Survey: ${label}`}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={metricColors[metric]}
                            fill={`${metricColors[metric]}30`}
                            strokeWidth={3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Survey Waves Table */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              All Survey Waves
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Grid container spacing={2}>
                {transformedWaves.map((wave, index) => (
                  <Grid item xs={12} key={index}>
                    <Card 
                      sx={{ 
                        border: wave.isLatestWave ? `2px solid ${brandColors.secondary}` : '1px solid #e0e0e0',
                        backgroundColor: wave.isLatestWave ? `${brandColors.secondary}10` : 'white'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Survey Wave {wave.FilmRecencyOrder}
                              {wave.isLatestWave && (
                                <Chip label="Latest" size="small" color="secondary" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Completed: {formatDateForDisplay(wave.surveyCompleteDate)} • 
                              {wave.weeksDisplay}
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.primary }}>
                            {wave.totalResponses.toLocaleString()} responses
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: getMetricColor('awareness') }}>
                                {wave.pctAware}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Awareness
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: getMetricColor('interest') }}>
                                {wave.pctInterest}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Interest
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: getMetricColor('theater') }}>
                                {wave.pctTheater}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Theater Intent
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: getMetricColor('pay') }}>
                                {wave.pctWillingToPay}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Willing to Pay
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Film Comps Tab */}
      {activeTab === 2 && (
        <Box>
          <FilmCompsContainer 
            filmId={ttId} 
            filmTitle={imdbData?.movie?.title_text || 'Unknown Film'}
          />
        </Box>
      )}

      {/* Box Office Projections Tab */}
      {activeTab === 3 && (
        <Box>
          <BoxOfficeProjectionsContainer 
            filmId={ttId}
            filmTitle={imdbData?.movie?.title_text || 'Unknown Film'}
          />
        </Box>
      )}

      {/* IMDB Information Tab */}
      {activeTab === 1 && (
        <Box>
          {imdbData ? (
            <Grid container spacing={3}>
              {/* Poster and Basic Info */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    {imdbData.movie?.primary_image_url && (
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <img
                          src={imdbData.movie.primary_image_url}
                          alt={imdbData.movie.title_text}
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}
                        />
                      </Box>
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {imdbData.movie?.title_text}
                    </Typography>
                    {imdbData.movie?.original_title_text !== imdbData.movie?.title_text && (
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                        Original: {imdbData.movie?.original_title_text}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip label={`${imdbData.movie?.release_year}`} size="small" />
                      <Chip label={imdbData.movie?.production_status} size="small" color="success" />
                      {imdbData.movie?.runtime_seconds && (
                        <Chip 
                          label={`${Math.round(imdbData.movie.runtime_seconds / 60)} min`} 
                          size="small" 
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* IMDB Details */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      IMDB Information
                    </Typography>
                    
                    {/* Ratings */}
                    {imdbData.ratings && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          Ratings & Performance
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f5c518' }}>
                                {imdbData.ratings.aggregate_rating || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                IMDB Rating
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.primary }}>
                                {imdbData.ratings.vote_count?.toLocaleString() || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Vote Count
                              </Typography>
                            </Box>
                          </Grid>
                          {imdbData.ratings.box_office?.domestic_lifetime && (
                            <>
                              <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.success }}>
                                    ${(imdbData.ratings.box_office.domestic_lifetime.total_amount / 1000000).toFixed(0)}M
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Domestic Box Office
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.warning }}>
                                    #{imdbData.ratings.box_office.domestic_lifetime.rank}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Box Office Rank
                                  </Typography>
                                </Box>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Production Details */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Production Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Release Date:</strong> {formatDateForDisplay(imdbData.movie?.release_date)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Production Status:</strong> {imdbData.movie?.production_status}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Type:</strong> {imdbData.movie?.title_type}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {imdbData.ratings?.box_office?.production_budget && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Budget:</strong> ${(imdbData.ratings.box_office.production_budget.amount / 1000000).toFixed(0)}M
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            <strong>Runtime:</strong> {imdbData.movie?.runtime_seconds ? 
                              `${Math.round(imdbData.movie.runtime_seconds / 60)} minutes` : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Adult Content:</strong> {imdbData.movie?.is_adult ? 'Yes' : 'No'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              IMDB information not available for this film.
            </Alert>
           )}
         </Box>
       )}
          </>
        ) : (
          <BlurredContent
            requiredTier={FILM_TIERS.PROFESSIONAL}
            featureName="Film Analytics"
            blurAmount={10}
          >
            <Box>
              {/* Show a preview of the content that's available */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Survey Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed survey wave analysis, trends, and demographic insights
                </Typography>
              </Paper>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Film Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  IMDB data, cast, crew, ratings, and production details
                </Typography>
              </Paper>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Film Comps
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare with similar films and benchmark performance
                </Typography>
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Box Office Projections
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revenue forecasts and market performance predictions
                </Typography>
              </Paper>
            </Box>
          </BlurredContent>
        )
       )}

      {/* Deep Dive Tab removed - feature moved to standalone page at /film-tracking/deep-dive */}

      {/* Removed sections documented in REMOVED_SECTIONS.md */}
     </Container>
   );
 };
 
 export default FilmDetail;

/* eslint-enable no-unused-vars */
